import React from 'react'
import PropTypes from 'prop-types'
import { Box, Media, Logo, Text, P, Checkbox, Price, Disclosure } from '@deseretbook/react-ui'

/**
 * Membership addons form fields for the subscriptions signup. This looks at all the available
 * memberships and renders addon fields for users to include them as part of their subscription
 * signup.
 *
 * @returns {function} Component
 */
const MembershipAddonsFormField = ({ subscriptionForm }) => {
  const { selectedEstimate } = subscriptionForm
  // Get all of the different membership discounts for the selected estimate
  const { availableMembershipDiscounts = [] } = (
    selectedEstimate && selectedEstimate.subscriptionPlan
  ) || {}
  // Once we have all the different discounts, we see if any are already applied.
  const appliedMembershipDiscount = selectedEstimate && availableMembershipDiscounts
    .find(membershipDiscount => (
      selectedEstimate.discounts
        .some(appliedDiscount => appliedDiscount.code === membershipDiscount.code)
    ))

  const [hasAddons] = React.useState((
    !appliedMembershipDiscount || !!subscriptionForm.form.addonSubscriptions.length
  ))

  // If there is in fact a membership discount that's being applied then we don't want to up-sell
  // anything since they're already getting the discount.
  if (!hasAddons) {
    return null
  }

  return Object.entries(subscriptionForm.membershipDiscountPlans || {})
    .map(([planType, plans]) => {
      // Figure out if there's a logo associated with this subscription plan addon.
      let logoName

      if (planType === 'SubscriptionPlan::Platinum') {
        logoName = 'platinumCrest'
      }

      // Figure out the estimated discount that the user will get by subscribing to this addon.
      const discount = selectedEstimate
        && selectedEstimate.subscriptionPlan
        && selectedEstimate.subscriptionPlan.availableMembershipDiscounts
          .find(({ sourcePlanType }) => sourcePlanType === planType)

      const selectedEstimateWillBillImmediately = selectedEstimate
        && selectedEstimate.firstBillingDate
        && new Date(`${selectedEstimate.firstBillingDate} 00:00:00`).toDateString() === new Date().toDateString()
      const firstBillingDate = selectedEstimate
        && new Date(`${selectedEstimate.firstBillingDate} 00:00:00`)
          .toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })

      const addonPlan = plans[0]

      // Calculate the discount percentage if we can.
      const discountPercentage = discount && discount.amount
        && Math.round(100 - ((
          (selectedEstimate.subscriptionPlan.price - discount.amount)
          / selectedEstimate.subscriptionPlan.price
        ) * 100))

      return (
        <Box padding={1} paddingTop={2} background="alternate" key={planType}>
          <Media>
            {!!logoName && (
              <Media.Figure>
                <Logo name={logoName} size="large" />
              </Media.Figure>
            )}
            <Media.Content>
              <Text isBold hasEllipsis>{addonPlan.name} Discount</Text>
              <P>
                You could {discountPercentage
                  ? <Text isBold>save {discountPercentage}% more</Text>
                  : 'save'} on {(selectedEstimate
                    && selectedEstimate.subscriptionPlan
                    && selectedEstimate.subscriptionPlan.name) || 'your subscription'
                } by subscribing to {addonPlan.name}.
              </P>

              <Box paddingBottom={0.5}>
                <Disclosure disabled={subscriptionForm.isLoading}>
                  <Disclosure.Header>Learn more</Disclosure.Header>
                  <Disclosure.Content>
                    <P>
                      <Text size="small">
                        <Text isBold size="small">Already have {addonPlan.name}?</Text> Make sure
                        you&#39;re logged in to your {addonPlan.name} account, or contact us for
                        help merging your accounts.
                      </Text>
                    </P>
                    {!!selectedEstimateWillBillImmediately && firstBillingDate && (
                      <P>
                        <Text size="small">
                          You have until <Text size="small" isBold>{firstBillingDate}</Text> to signup
                          to receive this discount before your first payment.
                        </Text>
                      </P>
                    )}
                  </Disclosure.Content>
                </Disclosure>
              </Box>

              <Checkbox
                name="subscriptionAddon"
                value={addonPlan.btPlanId}
                checked={subscriptionForm.form.addonSubscriptions.includes(addonPlan.btPlanId)}
                onChange={subscriptionForm.onFormChange}
                disabled={subscriptionForm.isLoading}
              >
                <Text>Include {addonPlan.name} for just <Price value={addonPlan.price} /> a {
                  addonPlan.monthly
                    ? 'month'
                    : 'year'
                  }.
                </Text>
              </Checkbox>
            </Media.Content>
          </Media>
        </Box>
      )
    })
}

MembershipAddonsFormField.propTypes = {
  subscriptionForm: PropTypes.shape({
    form: PropTypes.shape({}),
  }).isRequired,
}

export default MembershipAddonsFormField
